import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, RefreshCw, ArrowRight, LogOut, CheckCircle } from "lucide-react";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";

export default function VerifyPending() {
    const navigate = useNavigate();
    const { logout } = useAuth();
    const [loading, setLoading] = useState(false);
    const [resendStatus, setResendStatus] = useState("idle"); // idle, sending, sent, error
    const [userEmail, setUserEmail] = useState("");
    const [skipped, setSkipped] = useState(false);

    // Poll for Verification Status
    useEffect(() => {
        let isMounted = true;
        let intervalId;

        const checkStatus = async () => {
            try {
                const { data } = await api.get("/auth/me");
                if (isMounted) setUserEmail(data.email);

                if (data.isEmailVerified) {
                    // Success! Redirect to finish onboarding
                    navigate("/signup-flow", { replace: true });
                }
            } catch (err) {
                console.error("Polling error:", err);
                // If 401, they might need to login again
                if (err.response?.status === 401) {
                    navigate("/login");
                }
            }
        };

        // Check immediately
        checkStatus();

        // Then poll every 3 seconds
        intervalId = setInterval(checkStatus, 3000);

        return () => {
            isMounted = false;
            clearInterval(intervalId);
        };
    }, [navigate]);

    const handleResend = async () => {
        setResendStatus("sending");
        try {
            await api.post("/auth/send-verification", { origin: window.location.origin });
            setResendStatus("sent");
            setTimeout(() => setResendStatus("idle"), 5000);
        } catch (err) {
            // Ignore "already_verified" error as it might race with polling
            if (err.response?.data?.error === "already_verified") {
                navigate("/signup-flow");
            } else {
                console.error("Resend error:", err);
                setResendStatus("error");
            }
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate("/signup");
    };

    const handleSkip = () => {
        setSkipped(true);
        navigate("/signup-flow", { replace: true });
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-6">
            <div className="max-w-md w-full text-center space-y-8">

                {/* Animation Icon */}
                <div className="relative inline-block">
                    <div className="absolute inset-0 bg-brand-blue/20 rounded-full blur-xl animate-pulse"></div>
                    <div className="relative bg-white dark:bg-gray-800 p-6 rounded-full shadow-2xl border border-gray-100 dark:border-gray-700">
                        <Mail className="w-12 h-12 text-brand-blue" />
                    </div>
                    {/* Status Badge */}
                    <div className="absolute -bottom-2 -right-2 bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1 rounded-full border border-amber-200 flex items-center gap-1 shadow-sm">
                        <RefreshCw className="w-3 h-3 animate-spin" /> Waiting...
                    </div>
                </div>

                <div className="space-y-4">
                    <h1 className="text-3xl font-display font-bold text-gray-900 dark:text-white">
                        Verify your Soul-Signal
                    </h1>
                    <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed">
                        We sent a magical link to <br />
                        <span className="font-semibold text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">{userEmail || "your email"}</span>
                    </p>
                    <p className="text-sm text-gray-500 max-w-xs mx-auto">
                        Please click the link in that email to prove you are human. We will auto-detect it here.
                    </p>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-4 max-w-sm mx-auto pt-4">
                    <button
                        onClick={handleResend}
                        disabled={resendStatus === "sending" || resendStatus === "sent"}
                        className={`w-full py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2
                    ${resendStatus === "sent"
                                ? "bg-green-50 text-green-700 border border-green-200"
                                : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-750"
                            }`}
                    >
                        {resendStatus === "sending" && <RefreshCw className="w-4 h-4 animate-spin" />}
                        {resendStatus === "sent" && <CheckCircle className="w-4 h-4" />}
                        {resendStatus === "idle" && "Resend Email"}
                        {resendStatus === "sending" && "Sending..."}
                        {resendStatus === "sent" && "Sent!"}
                        {resendStatus === "error" && "Error - Try again"}
                    </button>

                    <button
                        onClick={handleSkip}
                        className="text-sm text-brand-blue font-semibold hover:text-blue-700 transition-colors flex items-center justify-center py-2"
                    >
                        In a hurry? Skip for now.
                    </button>

                    <button
                        onClick={handleLogout}
                        className="text-sm text-gray-400 hover:text-red-500 transition-colors flex items-center justify-center gap-1 mt-2"
                    >
                        <LogOut size={14} /> Wrong email? Start over
                    </button>
                </div>

            </div>
        </div>
    );
}
